/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'
import { usersApiService } from '../../../services/api/users'
import { useMarkNotificationsAsViewed } from '../../../hooks/useNotifications'
import moment from 'moment'
import _ from 'lodash'
import { validateFileFactory } from '~/services/factory/validateFileFactory'
import { formatUserDateTime } from '~/utils/dateFormatting'
import { useModal } from '~/contexts/ModalContext'
import { EosWorkflowInstructionsReview } from '~/pages/eos/setup-users-and-workflows'

interface NotificationMessage {
  fromUser?: number
  fromUserObj?: any
  message: string
}

interface NotificationItem {
  id: number
  seen: boolean
  subject: string
  message: NotificationMessage
  timeSent?: number
  expirationTime?: number
}

interface ReadNotificationProps {
  notification: NotificationItem
  onClose?: () => void
}

const ReadNotification: React.FC<ReadNotificationProps> = ({ notification, onClose }) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const queryClient = useQueryClient()
  // Modal always opens centered
  const [isOpen, setIsOpen] = useState(true)
  const [fromUser, setFromUser] = useState<any>(null)
  const { mutate: markAsViewed } = useMarkNotificationsAsViewed()
  const validateFile = validateFileFactory()
  const { showModal } = useModal()

  useEffect(() => {
    if (!notification.seen) {
      notification.seen = true
      markAsViewed(
        { notificationIds: notification.id },
        {
          onSuccess: () => {
            window.dispatchEvent(new CustomEvent('updateNotificationsCount'))
            // Force refetch of all notification-related queries
            queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
            queryClient.invalidateQueries({ queryKey: ['notification-count'] })
            queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
          }
        }
      )
    }

    // Fetch user info
    usersApiService.getAssembledUsersGroups().then((data) => {
      const userObj = data.mergedAll?.find((user: any) => user.id === notification.message.fromUser)
      setFromUser(userObj)
    })

    // Handle EOS workflow instructions and downloadable links
    setTimeout(() => {
      const notifBody = document.getElementById('notifBody')
      if (!notifBody) return

      // EOS workflow instructions
      const eosLinks = notifBody.querySelectorAll('span.eos-wf-instruction-admin + a')
      eosLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault()
          const url = (event.target as HTMLAnchorElement).href
          const urlSplitArray = url.split('/')
          const instructionsData = {
            uuid: urlSplitArray[urlSplitArray.length - 2],
            lastUpdatedAt: parseInt(urlSplitArray[urlSplitArray.length - 1]) * 1000
          }
          showModal(<EosWorkflowInstructionsReview instructionsData={instructionsData} />)
          doClose()
        })
      })

      // Downloadable files
      const downloadLink = notifBody.querySelector('a.downloadable')
      if (downloadLink) {
        downloadLink.addEventListener('click', (e) => {
          const target = e.target as HTMLAnchorElement
          const fileIdArr = target.getAttribute('data-fileId')
          const linkUrl = target.getAttribute('href') ?? ''
          const sharedLinkData: any = {}
          sharedLinkData.isSharedLinkUrl = linkUrl.includes('/app/shared-link/')
          sharedLinkData.sharedLinkUUID = sharedLinkData.isSharedLinkUrl
            ? linkUrl.split('/app/shared-link/')[1].substring(0, 36)
            : ''
          sharedLinkData.sharedLinkTenantUUID = sharedLinkData.isSharedLinkUrl
            ? linkUrl.split('tenantUuid=')[1].substring(0, 36)
            : ''
          if (fileIdArr) {
            e.preventDefault()
            validateFile.downloadOriginal(Number(fileIdArr), { newTab: false }, sharedLinkData)
          }
        })
      }
    }, 100)
  }, [])

  // ...drag/drop effect removed; now handled by Modal

  const doClose = () => {
    setIsOpen(false)
    if (_.isFunction(onClose)) {
      onClose()
    }
  }

  const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return translate('NOT_AVAILABLE')
    return moment(timestamp).format('YYYY/MM/DD HH:mm:ss A')
  }

  return (
    <Modal
      className="modal-content"
      show={isOpen}
      handleClose={doClose}
      title={translate('NOTIFICATIONS_DETAIL_H_TITLE')}
      draggable={true}
    >
      <Modal.Body>
        <table className="file-details-notif" style={{ border: 'none', fontSize: '12px' }}>
          <tbody>
            <tr>
              <td className="param">
                <b>{translate('NOTIFICATIONS_DETAIL_TH_FROM')}</b>
                <br />
                {fromUser ? fromUser.fullName : translate('SYSTEM_USER')}
              </td>
            </tr>
            <tr>
              <td className="param" style={{ width: '240px' }}>
                <b>{translate('NOTIFICATIONS_DETAIL_TH_SUBJECT')}</b>
                <br />
                {notification.subject}
              </td>
            </tr>
            <tr>
              <td className="param" style={{ width: '240px' }}>
                <b>{translate('NOTIFICATIONS_DETAIL_TH_BODY')}</b>
                <br />
                <div id="notifBody" dangerouslySetInnerHTML={{ __html: notification.message.message }}></div>
              </td>
            </tr>
            <tr>
              <td className="param">
                <b>{translate('NOTIFICATIONS_DETAIL_TH_DATE_SENT')}</b>
                <br />
                {notification.timeSent ? formatUserDateTime(notification.timeSent) : translate('NOT_AVAILABLE')}
              </td>
            </tr>
            {notification.expirationTime && (
              <tr>
                <td className="param" style={{ width: '240px' }}>
                  <b>{translate('NOTIFICATIONS_DETAIL_TH_EXPIRATION_DATE')}</b>
                  <br />
                  {formatUserDateTime(notification.expirationTime)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="primary" onClick={doClose} data-action="save">
          {translate('OK')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ReadNotification
